import { Head, Link, router, usePage } from "@inertiajs/react"
import { AlertCircle, CheckCircle2, Folder, Tag } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ClipsSubTabs } from "@/components/clips/ClipsSubTabs"
import { ClipTagPills } from "@/components/clips/ClipTagPills"
import { CyclingThumbnail } from "@/components/clips/CyclingThumbnail"
import type { Clip } from "@/components/clips/types"
import type { PageProps } from "@/types/inertia"

type EnrichmentsLogProps = PageProps<{
  low_confidence: Clip[]
  standard:       Clip[]
  total_count:    number
}>

export default function EnrichmentsLog() {
  const { props } = usePage<EnrichmentsLogProps>()
  const { low_confidence, standard, total_count } = props

  return (
    <>
      <Head title="Enrichments log">
        <meta name="description" content="Review clips the inbox organizer agent has enriched. Low-confidence entries stay until you review them; everything else auto-clears after 14 days." />
        <meta property="og:title" content="Enrichments log" />
        <meta property="og:description" content="Review clips the inbox organizer agent has enriched." />
      </Head>
      <AppShell maxWidth="6xl">
        <PageHeader
          title="Clips"
          description="Review what the inbox organizer agent wrote. Entries auto-clear after 14 days unless flagged as low-confidence."
          actions={
            <Button
              size="sm"
              variant="secondary"
              disabled={total_count === 0}
              onClick={() => {
                if (window.confirm(`Mark all ${total_count} clip${total_count === 1 ? "" : "s"} as reviewed?`)) {
                  router.post("/clips/bulk_mark_reviewed", {}, { preserveScroll: true })
                }
              }}
            >
              <CheckCircle2 className="mr-1 h-4 w-4" />
              Mark all as reviewed
            </Button>
          }
        />

        <div className="mt-6 space-y-6">
          <ClipsSubTabs active="enrichments_log" awaitingReviewCount={total_count} />

          {total_count === 0 && <EmptyState />}

          {low_confidence.length > 0 && (
            <section className="space-y-3">
              <header className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-signal-display" />
                <h2 className="m-0">Flagged: low confidence ({low_confidence.length})</h2>
              </header>
              <p className="m-0 text-sm text-ink-muted">
                The agent wasn't confident about these enrichments. They stay here until you review them.
              </p>
              <ul className="space-y-3">
                {low_confidence.map((clip) => (
                  <EntryRow key={clip.id} clip={clip} flagged />
                ))}
              </ul>
            </section>
          )}

          {standard.length > 0 && (
            <ul className="space-y-3">
              {standard.map((clip) => (
                <EntryRow key={clip.id} clip={clip} />
              ))}
            </ul>
          )}
        </div>
      </AppShell>
    </>
  )
}

function EntryRow({ clip, flagged = false }: { clip: Clip; flagged?: boolean }) {
  function markReviewed() {
    router.patch(`/clips/${clip.id}/mark_reviewed`, {}, { preserveScroll: true })
  }

  return (
    <li
      className={`flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:gap-4 sm:pr-5 ${
        flagged ? "border-signal bg-signal-faded" : "border-hairline bg-surface"
      }`}
    >
      <Link
        href={`/clips/${clip.id}`}
        className="block w-40 shrink-0 no-underline"
      >
        <CyclingThumbnail thumbnails={clip.thumbnails} title={clip.title ?? "Clip"} emptyLabel={null} />
      </Link>

      <div className="min-w-0 flex-1">
        {clip.enriched_at && (
          <p className="m-0 text-xs text-ink-muted">enriched {timeAgo(clip.enriched_at)}</p>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <Link
            href={`/clips/${clip.id}`}
            className="font-medium text-ink-display no-underline hover:text-accent-display"
          >
            {clip.title || clip.current_filename || "Untitled"}
          </Link>
          {flagged && <Badge tone="signal">Low confidence</Badge>}
        </div>
        {clip.short_description && (
          <p className="m-0 mt-0.5 line-clamp-2 text-sm text-ink-muted">{clip.short_description}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-muted">
          <span className="inline-flex items-center gap-1.5" title={clip.current_folder_path ?? ""}>
            <Folder className="h-3 w-3 shrink-0" />
            <span>{clip.current_folder_path || "/"}</span>
          </span>
          {clip.tag_names.length > 0 && (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <Tag className="h-3 w-3 shrink-0" />
              <ClipTagPills tags={clip.tag_names} />
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-5">
        <Link
          href={`/clips/${clip.id}`}
          className="text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          View clip
        </Link>
        <Button size="sm" variant="primary" onClick={markReviewed}>
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          Mark reviewed
        </Button>
      </div>
    </li>
  )
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-hairline bg-surface p-8 text-center text-ink-muted">
      <p className="m-0">No enrichments to review.</p>
      <p className="m-0 mt-1 text-xs">
        Drop a clip into your Dropbox <code>_inbox/</code> or click "Mark for enrichment" on a clip to queue work for the inbox organizer agent.
      </p>
    </div>
  )
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "recently"
  const secs = Math.floor((Date.now() - then) / 1000)
  if (secs < 60) return "just now"
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  const days = Math.floor(secs / 86400)
  return `${days}d ago`
}
